/**
 * `companies.benefits_and_perks` is a text column holding one benefit per line.
 *
 * A jsonb list was considered and dropped: the icons are derived from the text
 * anyway, so structure would buy nothing that a newline does not already give.
 */

/**
 * Splits the stored column. The newline is the canonical separator; semicolons
 * and commas are a **fallback for rows written before the convention existed**,
 * and only when no newline is present.
 *
 * Order matters. Applying the comma rule to text that already has newlines
 * would cut "Assicurazione sanitaria, anche per la famiglia" in two; applying
 * it to a single legacy line is the only way that row renders as more than one
 * benefit at all. The first save on such a company rewrites it in the canonical
 * form, so the fallback is a one-way door.
 */
export const parseBenefits = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  const separator = /\r?\n/.test(raw) ? /\r?\n/ : /[;,]/;
  return raw
    .split(separator)
    .map((line) => line.trim())
    .filter(Boolean);
};

/**
 * Normalises **on the way out only**.
 *
 * The textarea keeps whatever the user typed, blank lines and trailing spaces
 * included: rewriting the field under the cursor is exactly the interruption
 * EC8 forbids. What reaches the column is the tidy version; what stays on
 * screen is theirs.
 *
 * Nothing is deduplicated and nothing is truncated — the live chip preview is
 * the check, so a duplicate shows up as two chips instead of vanishing (EC9).
 */
export const serializeBenefits = (raw: string): string | null => {
  const lines = parseBenefits(raw);
  return lines.length > 0 ? lines.join("\n") : null;
};

/**
 * The icon comes from the words, so there is no icon picker to build and no
 * per-benefit choice to store. An unmatched benefit gets a neutral mark rather
 * than no icon, so the grid on the public page stays even.
 */
const ICON_RULES: readonly [RegExp, string][] = [
  [/formazion|corsi|studio|learning|libri/i, "ph-graduation-cap"],
  [/orario|flessib/i, "ph-clock"],
  [/remote|remoto|casa|smart/i, "ph-house-line"],
  [/buoni|pasto|pranzo|mensa/i, "ph-fork-knife"],
  [/salute|sanit|assicura|welfare/i, "ph-heartbeat"],
  [/laptop|mac|computer|setup|attrezzatur/i, "ph-laptop"],
  [/ferie|vacanz|venerd|estiv/i, "ph-sun"],
  [/retreat|viaggio|offsite|team building/i, "ph-airplane-tilt"],
  [/bonus|premio|stock|equity/i, "ph-gift"],
  [/palestra|sport|bici/i, "ph-barbell"],
];

export const benefitIcon = (label: string) =>
  ICON_RULES.find(([pattern]) => pattern.test(label))?.[1] ?? "ph-sparkle";

/** A line long enough that it reads as a paragraph, not a benefit (EC10). */
export const BENEFIT_LONG_LINE_LENGTH = 60;
